import { Link } from '@tanstack/react-router'

import {
  calloutStatusOrder,
  calloutStatusDescriptions,
  formatCalloutStatus,
} from '@/help/calloutStatusChanges'
import { getAllowedCalloutStatusTransitions } from '@/db/domain/calloutStatus'

export function HelpCalloutStatusChangesScreen() {
  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="mx-auto w-full max-w-container px-4 py-8 space-y-6">
        <header className="space-y-2">
          <div className="flex items-center gap-3">
            <Link
              to="/help"
              className="text-sm font-medium text-slate-700 hover:text-slate-900 underline underline-offset-4"
            >
              Help
            </Link>
            <span className="text-sm text-slate-400">/</span>
            <span className="text-sm text-slate-600">
              Callout status changes
            </span>
          </div>
          <h1 className="text-2xl font-semibold">Callout status changes</h1>
          <p className="text-sm text-slate-600">
            Callouts move through a lifecycle. The status selector only shows
            allowed next states, and every change is recorded in the Callout’s
            status history.
          </p>
        </header>

        <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
          <h2 className="text-base font-semibold">Where status is changed</h2>
          <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
            <li>
              On a Callout’s details page, in the <span className="font-medium">Status</span>{' '}
              section (includes the status history list).
            </li>
            <li>
              On the technician <span className="font-medium">Log work</span>{' '}
              screen via quick actions (for example <span className="font-medium">LeftSite</span>{' '}
              and <span className="font-medium">Arrived</span>).
            </li>
            <li>
              During invoicing, the callout is typically moved from{' '}
              <span className="font-medium">Ready to Invoice</span> to{' '}
              <span className="font-medium">Invoiced</span>.
            </li>
          </ul>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
          <h2 className="text-base font-semibold">Statuses</h2>
          <dl className="grid gap-3 sm:grid-cols-2">
            {calloutStatusOrder.map((status) => (
              <div
                key={status}
                className="rounded-lg border border-slate-200 bg-slate-50 p-3"
              >
                <dt className="text-sm font-semibold text-slate-900">
                  {formatCalloutStatus(status)}
                </dt>
                <dd className="mt-1 text-sm text-slate-700">
                  {calloutStatusDescriptions[status]}
                </dd>
              </div>
            ))}
          </dl>
          <p className="text-xs text-slate-500">
            Note: In the technician UI, the button labelled <span className="font-medium">Arrived</span>{' '}
            sets the status to <span className="font-medium">Arrived</span>.
          </p>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
          <h2 className="text-base font-semibold">Allowed transitions</h2>
          <p className="text-sm text-slate-700">
            From each status, you can only move to the following next statuses:
          </p>

          <div className="overflow-x-auto">
            <table className="min-w-full border-separate border-spacing-0 text-left text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-white border-b border-slate-200 px-3 py-2 font-semibold text-slate-800">
                    Current
                  </th>
                  <th className="border-b border-slate-200 px-3 py-2 font-semibold text-slate-800">
                    Can move to
                  </th>
                </tr>
              </thead>
              <tbody>
                {calloutStatusOrder.map((status) => {
                  const allowed = getAllowedCalloutStatusTransitions(status)
                  return (
                    <tr key={status}>
                      <td className="sticky left-0 bg-white border-b border-slate-100 px-3 py-2 font-medium text-slate-900">
                        {formatCalloutStatus(status)}
                      </td>
                      <td className="border-b border-slate-100 px-3 py-2 text-slate-700">
                        {allowed.length > 0
                          ? allowed
                              .map((next) => formatCalloutStatus(next))
                              .join(', ')
                          : '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            <p className="font-medium text-slate-900">
              Special cases to know
            </p>
            <ul className="mt-2 list-disc pl-5 space-y-1">
              <li>
                When a callout is <span className="font-medium">Invoiced</span>,
                it is read-only. An admin can move it back to{' '}
                <span className="font-medium">Review</span> to resolve issues.
              </li>
              <li>
                A <span className="font-medium">Cancelled</span> callout can be
                moved back to <span className="font-medium">Booked</span> if it
                needs to be re-scheduled.
              </li>
            </ul>
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
          <h2 className="text-base font-semibold">What gets recorded</h2>
          <p className="text-sm text-slate-700">
            Each time you change a Callout status, the system records a status
            change entry (shown in the Callout details page).
          </p>
          <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
            <li>
              The <span className="font-medium">previous status</span> and the{' '}
              <span className="font-medium">new status</span>.
            </li>
            <li>
              <span className="font-medium">Who</span> made the change.
            </li>
            <li>
              <span className="font-medium">When</span> it happened.
            </li>
          </ul>
        </section>
      </main>
    </div>
  )
}
